import React, { useState } from 'react';
import type { Collection } from '../domain/collections';
import type { Listing } from '../domain/listings';
import { useLoad } from '../hooks/useLoad';
import { useRepositories } from '../hooks/useRepositories';
import { ActionError, ErrorState } from './PageStates';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Skeleton } from '@/components/ui/skeleton';

/**
 * Copy a listing into another of my collections (016 FR-018–FR-021): a new
 * listing with the same content and photos, and no comments. Moving is a
 * copy and then deleting the original; the copy never says where it came from.
 */
export default function CloneDialog({
  listing,
  open,
  onOpenChange,
  onCloned,
}: {
  listing: Listing;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCloned: (copy: Listing) => void;
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-3xl border-none bg-background">
        <DialogHeader>
          <DialogTitle className="text-xl">Copy to another collection</DialogTitle>
          <DialogDescription>
            This makes a new listing with the same details and photos. Comments stay behind. To move
            it, delete this one after copying.
          </DialogDescription>
        </DialogHeader>
        {open && <CloneForm listing={listing} onCloned={onCloned} />}
      </DialogContent>
    </Dialog>
  );
}

function CloneForm({ listing, onCloned }: { listing: Listing; onCloned: (copy: Listing) => void }) {
  const { collections, listings } = useRepositories();
  const mine = useLoad<Collection[]>(() => collections.list('owner'), 'owned');
  const [target, setTarget] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);

  if (mine.status === 'loading') {
    return (
      <div role="status" aria-label="Loading your collections" className="space-y-2">
        <Skeleton className="h-12 rounded-2xl" />
        <Skeleton className="h-12 rounded-2xl" />
      </div>
    );
  }
  if (mine.status === 'error') {
    return <ErrorState message="Couldn't load your collections." onRetry={mine.reload} />;
  }

  const others = mine.data.filter((c) => c.id !== listing.collection.id);

  const copy = async () => {
    if (!target) return;
    setBusy(true);
    setError(null);
    try {
      onCloned(await listings.clone(listing.id, target));
    } catch (e) {
      setError(e);
      setBusy(false);
    }
  };

  if (others.length === 0) {
    return (
      <p className="rounded-2xl bg-muted px-4 py-3 text-sm text-muted-foreground">
        You have no other collection to copy it into.
      </p>
    );
  }

  return (
    <div className="space-y-4">
      <fieldset>
        <legend className="mb-3 text-sm font-semibold">Copy into</legend>
        <div className="max-h-72 space-y-2 overflow-y-auto">
          {others.map((c) => (
            <label
              key={c.id}
              className="flex cursor-pointer items-center gap-3 rounded-2xl bg-muted px-4 py-3"
            >
              <input
                type="radio"
                name="clone-target"
                value={c.id}
                checked={target === c.id}
                onChange={() => setTarget(c.id)}
                className="size-4 accent-primary"
              />
              <span className="font-medium">{c.name}</span>
            </label>
          ))}
        </div>
      </fieldset>
      {error !== null && <ActionError error={error} />}
      <Button className="w-full rounded-full" disabled={!target || busy} onClick={copy}>
        {busy ? 'Copying…' : 'Copy'}
      </Button>
    </div>
  );
}
